import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Photo, SlideshowConfig } from '../../types';
import {
  COLLAGE_SIZES,
  MOSAIC_ARRANGEMENTS,
  MOSAIC_RESHUFFLE_MULTIPLIER,
  MIXED_COLLAGE_DWELL_MULTIPLIER,
  CELL_DELAY_MIN_FACTOR,
  CELL_DELAY_MAX_FACTOR,
  getSlideMotion,
  effectiveFit,
  isCollageSlideFor,
  kenBurnsDrift,
} from '../../lib/slideshowPresentation';
import styles from './VenueDisplayPreview.module.css';

/**
 * The TV stage — a faithful, continuously-running rendering of what guests
 * will see. It is the source of truth of the Display Studio: every inspector
 * control changes this, immediately.
 *
 * Position is *controlled* by the studio so the manual ‹ › controls and the
 * auto-advance timer drive the same cursor. All presentation rules come from
 * lib/slideshowPresentation — the same module the real display uses — so the
 * two can never drift apart.
 */

/**
 * Same source the real display uses. Thumbnails are 400px wide (THUMBNAIL_WIDTH)
 * and the stage renders up to ~900px — ~1400px in fullscreen — so preferring
 * them here upscaled every photo 2–3x and made the preview look worse than the
 * TV it is meant to represent.
 */
function photoSrc(photo: Photo): string {
  return photo.originalUrl ?? photo.thumbnailUrl ?? '';
}

const orientationCache = new Map<string, boolean>();

function useOrientation(photoId: string) {
  const [isPortrait, setIsPortrait] = useState<boolean | null>(
    () => orientationCache.get(photoId) ?? null,
  );
  const [seen, setSeen] = useState(photoId);
  if (photoId !== seen) {
    setSeen(photoId);
    setIsPortrait(orientationCache.get(photoId) ?? null);
  }
  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (!img.naturalWidth || !img.naturalHeight) return;
    const portrait = img.naturalHeight > img.naturalWidth;
    orientationCache.set(photoId, portrait);
    setIsPortrait(portrait);
  };
  return { isPortrait, handleLoad };
}

function objectFitFor(config: SlideshowConfig, isPortrait: boolean | null) {
  const fit = effectiveFit(config.photoFit, isPortrait, config.preservePortrait);
  return fit === 'fit' ? 'contain' : fit === 'stretch' ? 'fill' : 'cover';
}

function StagePhoto({ photo, config }: { photo: Photo; config: SlideshowConfig }) {
  const { isPortrait, handleLoad } = useOrientation(photo._id);
  const drift = useMemo(() => kenBurnsDrift(photo._id), [photo._id]);
  const objectFit = objectFitFor(config, isPortrait);
  const style: React.CSSProperties =
    objectFit === 'contain'
      ? { width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%', objectFit }
      : { width: '100%', height: '100%', objectFit };

  if (config.transition === 'kenburns') {
    return (
      <motion.img
        src={photoSrc(photo)}
        alt=""
        onLoad={handleLoad}
        className={styles['photo']}
        style={style}
        draggable={false}
        initial={{ scale: 1, x: '0%', y: '0%' }}
        animate={{ scale: 1.09, x: `${drift.x}%`, y: `${drift.y}%` }}
        transition={{ duration: config.intervalSeconds + 1.2, ease: 'linear' }}
      />
    );
  }

  return (
    <img
      src={photoSrc(photo)}
      alt=""
      onLoad={handleLoad}
      className={styles['photo']}
      style={style}
      draggable={false}
    />
  );
}

function StageCell({ photo, config }: { photo: Photo; config: SlideshowConfig }) {
  const { isPortrait, handleLoad } = useOrientation(photo._id);
  return (
    <motion.img
      src={photoSrc(photo)}
      alt=""
      onLoad={handleLoad}
      className={styles['cell-photo']}
      style={{ objectFit: objectFitFor(config, isPortrait) }}
      draggable={false}
      initial={{ opacity: 0, scale: 1.05 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.99 }}
      transition={{ duration: 0.6, ease: 'easeInOut' }}
    />
  );
}

/** Collage whose cells refresh independently, mirroring the real display. */
function StageCollage({
  photos,
  config,
  seedIndex,
  seedNonce,
}: {
  photos: Photo[];
  config: SlideshowConfig;
  seedIndex: number;
  seedNonce: number;
}) {
  const layout = config.collageLayout;
  const cellCount = Math.max(1, Math.min(COLLAGE_SIZES[layout] ?? 4, photos.length));

  const [cells, setCells] = useState<Photo[]>([]);
  const [arrangement, setArrangement] = useState(0);
  const photosRef = useRef(photos);
  const cellsRef = useRef<Photo[]>([]);
  const cursorRef = useRef(seedIndex);
  photosRef.current = photos;

  useEffect(() => {
    const list = photosRef.current;
    if (list.length === 0) return;
    cursorRef.current = ((seedIndex % list.length) + list.length) % list.length;
    const seeded = Array.from({ length: cellCount }, (_, i) => list[(cursorRef.current + i) % list.length]);
    cursorRef.current = (cursorRef.current + cellCount - 1) % list.length;
    cellsRef.current = seeded;
    setCells(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellCount, seedNonce]);

  useEffect(() => {
    const list = photosRef.current;
    if (list.length <= cellCount) return;
    const baseMs = Math.max(1, config.intervalSeconds) * 1000;
    const timers: Array<ReturnType<typeof setTimeout>> = [];

    const randomDelay = () =>
      baseMs * (CELL_DELAY_MIN_FACTOR + Math.random() * (CELL_DELAY_MAX_FACTOR - CELL_DELAY_MIN_FACTOR));

    const pickNext = (): Photo => {
      const onScreen = new Set(cellsRef.current.map((c) => c._id));
      for (let attempt = 0; attempt < list.length; attempt++) {
        cursorRef.current = (cursorRef.current + 1) % list.length;
        const candidate = list[cursorRef.current];
        if (!onScreen.has(candidate._id)) return candidate;
      }
      return list[cursorRef.current];
    };

    for (let i = 0; i < cellCount; i++) {
      const schedule = (delay: number) => {
        timers[i] = setTimeout(function tick() {
          const picked = pickNext();
          cellsRef.current = cellsRef.current.map((c, idx) => (idx === i ? picked : c));
          setCells(cellsRef.current);
          timers[i] = setTimeout(tick, randomDelay());
        }, delay);
      };
      schedule(baseMs * (0.4 + i / cellCount) + Math.random() * baseMs * 0.4);
    }
    return () => timers.forEach(clearTimeout);
  }, [cellCount, config.intervalSeconds]);

  useEffect(() => {
    if (layout !== 'mosaic') return;
    const period = Math.max(1, config.intervalSeconds) * 1000 * MOSAIC_RESHUFFLE_MULTIPLIER;
    const id = setInterval(() => {
      setArrangement((prev) => {
        if (MOSAIC_ARRANGEMENTS.length < 2) return prev;
        let next = prev;
        while (next === prev) next = Math.floor(Math.random() * MOSAIC_ARRANGEMENTS.length);
        return next;
      });
    }, period);
    return () => clearInterval(id);
  }, [layout, config.intervalSeconds]);

  if (cells.length === 0) return null;
  const placements = layout === 'mosaic' ? MOSAIC_ARRANGEMENTS[arrangement] : null;

  return (
    <div className={[styles['collage'], styles[`collage-${layout}`]].join(' ')}>
      {cells.map((photo, i) => {
        const placement = placements?.[i];
        return (
          <motion.div
            key={`cell-${i}`}
            layout
            transition={{ type: 'spring', stiffness: 210, damping: 26 }}
            className={styles['cell']}
            style={
              placement
                ? {
                    gridColumn: `${placement.col} / span ${placement.colSpan}`,
                    gridRow: `${placement.row} / span ${placement.rowSpan}`,
                  }
                : undefined
            }
          >
            <AnimatePresence initial={false}>
              <StageCell key={photo._id} photo={photo} config={config} />
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}

export interface VenueDisplayPreviewProps {
  photos: Photo[];
  config: SlideshowConfig;
  albumName: string;
  eventName?: string;
  loading?: boolean;
  /** Controlled cursor — the studio owns it so ‹ › and the timer agree. */
  index: number;
  slideNumber: number;
  onAdvance: (direction: 1 | -1) => void;
  /** Empty-state action. */
  onAddPhotos?: () => void;
}

export function VenueDisplayPreview({
  photos,
  config,
  albumName,
  eventName,
  loading,
  index,
  slideNumber,
  onAdvance,
  onAddPhotos,
}: VenueDisplayPreviewProps) {
  const isLiveCollageMode = config.mode === 'collage';
  const isCollageSlide = isCollageSlideFor(config, slideNumber);
  // Latest callback without making it a timer dependency (which would restart
  // the slide clock on every parent render).
  const advanceRef = useRef(onAdvance);
  useEffect(() => {
    advanceRef.current = onAdvance;
  }, [onAdvance]);

  // Auto-advance. A live collage drives itself cell-by-cell, so it has no
  // slide to advance.
  useEffect(() => {
    if (photos.length <= 1 || isLiveCollageMode) return;
    const dwell = isCollageSlide ? MIXED_COLLAGE_DWELL_MULTIPLIER : 1;
    const id = setTimeout(
      () => advanceRef.current(1),
      Math.max(1, config.intervalSeconds) * dwell * 1000,
    );
    return () => clearTimeout(id);
  }, [photos.length, index, slideNumber, config.intervalSeconds, isCollageSlide, isLiveCollageMode]);

  const motionSpec = getSlideMotion(config.transition, 1);
  const current = photos.length > 0 ? photos[index % photos.length] : null;

  const slideKey = isLiveCollageMode
    ? 'live-collage'
    : isCollageSlide
      ? `collage-${slideNumber}`
      : (current?._id ?? 'empty');

  return (
    <div className={styles['tv']}>
      <div className={styles['screen']}>
        {loading ? (
          <div className={styles['state']}>
            <div className={styles['spinner']} />
            <p className={styles['state-title']}>Loading preview</p>
          </div>
        ) : photos.length === 0 ? (
          <div className={styles['state']}>
            <p className={styles['state-title']}>No photos yet</p>
            <p className={styles['state-body']}>Add photos to start your display.</p>
            {onAddPhotos && (
              <button type="button" onClick={onAddPhotos} className={styles['state-btn']}>
                Add photos
              </button>
            )}
          </div>
        ) : (
          <>
            <AnimatePresence initial={false}>
              <motion.div
                key={slideKey}
                initial={motionSpec.initial}
                animate={motionSpec.animate}
                exit={motionSpec.exit}
                transition={motionSpec.transition}
                className={styles['layer']}
              >
                {isCollageSlide ? (
                  <StageCollage
                    photos={photos}
                    config={config}
                    seedIndex={index}
                    seedNonce={isLiveCollageMode ? 0 : slideNumber}
                  />
                ) : (
                  current && <StagePhoto photo={current} config={config} />
                )}
              </motion.div>
            </AnimatePresence>

            {(config.showAlbumName || config.showEventName || config.showPhotoCount) && (
              <div className={styles['overlay']}>
                <div className={styles['overlay-text']}>
                  {config.showAlbumName && albumName && (
                    <span className={styles['overlay-album']}>{albumName}</span>
                  )}
                  {config.showEventName && eventName && (
                    <span className={styles['overlay-event']}>{eventName}</span>
                  )}
                </div>
                {config.showPhotoCount && (
                  <span className={styles['overlay-count']}>
                    {Math.min(index + 1, photos.length)} / {photos.length}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
